import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Snackbar,
  Paper,
  Divider,
  Chip,
  Avatar,
  Stack,
  IconButton,
  MenuItem,
} from '@mui/material'
import {
  Business as BusinessIcon,
  ContactMail as ContactIcon,
  LocationOn as LocationIcon,
  ReceiptLong as TaxIcon,
  Palette as BrandingIcon,
  AccountBalance as BankIcon,
  Description as DocumentNotesIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as TemplatesIcon,
  FormatListNumbered as NumberingIcon,
  PictureAsPdf as PreviewIcon,
  CloudUpload as UploadIcon,
  DeleteOutline as RemoveIcon,
} from '@mui/icons-material'
import { companyProfileService, CompanyProfile, BrandingAssetKind, PreviewKind } from '../../services/companyProfileService'
import InvoiceTemplateConfigurationPage from '../InvoiceTemplateConfigurationPage'
import DocumentSequenceConfigurationPage from '../DocumentSequenceConfigurationPage'

const TAB_NAMES = ['general', 'contact', 'address', 'tax', 'branding', 'bank', 'notes', 'templates', 'numbering', 'preview']
const PROFILE_TAB_COUNT = 7

const FIELD_LABELS: Record<string, string> = {
  company_name: 'Company name', address_line1: 'Address', city: 'City', state: 'State', state_code: 'GST state code',
  pincode: 'Pincode', gstin: 'GSTIN', pan: 'PAN', phone: 'Phone', whatsapp_number: 'WhatsApp number', email: 'Email',
  support_email: 'Support email', logo_url: 'Logo', bank_name: 'Bank name', bank_account_number: 'Bank account number',
  bank_ifsc: 'Bank IFSC', upi_id: 'UPI ID',
}

const PREVIEW_OPTIONS: { value: PreviewKind; label: string }[] = [
  { value: 'invoice', label: 'Tax Invoice' }, { value: 'estimate', label: 'Estimate' }, { value: 'sales_order', label: 'Sales Order' },
  { value: 'delivery_challan', label: 'Delivery Challan' }, { value: 'sales_return', label: 'Credit Note' },
  { value: 'purchase_order', label: 'Purchase Order' }, { value: 'purchase_bill', label: 'Purchase Bill' },
  { value: 'ledger', label: 'Customer Ledger Statement' }, { value: 'catalogue', label: 'Catalogue (showcase)' },
  { value: 'price_list', label: 'Wholesale Price List' },
]

const apiOrigin = (import.meta as any).env?.VITE_API_BASE_URL || ''
const assetSrc = (url?: string | null) => (url && url.startsWith('/') ? `${apiOrigin}${url}` : url || '')

const errorText = (err: any, fallback: string) => {
  const detail = err?.response?.data?.detail
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg || String(d)).join('; ')
  return typeof detail === 'string' ? detail : fallback
}

interface AssetUploaderProps {
  label: string
  helper: string
  kind: BrandingAssetKind
  url?: string | null
  onUploaded: (url: string) => void
  onRemove: () => void
  onError: (message: string) => void
}

const AssetUploader: React.FC<AssetUploaderProps> = ({ label, helper, kind, url, onUploaded, onRemove, onError }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const handleFile = async (file?: File) => {
    if (!file) return
    setBusy(true)
    try {
      const result = await companyProfileService.uploadAsset(kind, file)
      onUploaded(result.url)
    } catch (err: any) {
      onError(errorText(err, 'Upload failed'))
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary">{helper}</Typography>
      <Box sx={{ mt: 1.5, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: kind === 'dark_logo' ? '#1F2937' : '#F3F4F6', borderRadius: 1, overflow: 'hidden' }}>
        {url ? (
          <img src={assetSrc(url)} alt={label} style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }} />
        ) : (
          <Typography variant="caption" color="text.secondary">Not uploaded</Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
        <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <UploadIcon />} disabled={busy} onClick={() => inputRef.current?.click()}>
          {url ? 'Replace' : 'Upload'}
        </Button>
        {url && (
          <IconButton size="small" aria-label={`Remove ${label}`} onClick={onRemove}><RemoveIcon fontSize="small" /></IconButton>
        )}
      </Stack>
    </Paper>
  )
}

const PdfPreviewPanel: React.FC = () => {
  const [kind, setKind] = useState<PreviewKind>('invoice')
  const [format, setFormat] = useState('tally_prime')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [src, setSrc] = useState('')
  const isDocument = !['ledger', 'catalogue', 'price_list'].includes(kind)

  const generate = async () => {
    setLoading(true)
    setError('')
    try {
      const url = await companyProfileService.previewPdf(kind, isDocument ? format : 'tally_prime')
      setSrc((old) => { if (old) URL.revokeObjectURL(old); return url })
    } catch (err: any) {
      setError('Could not render the preview. Save your changes and make sure at least one active product exists for catalogue previews.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Previews use sample data with your <strong>saved</strong> branding and template settings. Save changes first, then generate.
      </Alert>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <TextField select size="small" label="Document" value={kind} onChange={(e) => setKind(e.target.value as PreviewKind)} sx={{ minWidth: 240 }}>
          {PREVIEW_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {isDocument && (
          <TextField select size="small" label="Printer format" value={format} onChange={(e) => setFormat(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="tally_prime">A4 (Tally Prime style)</MenuItem>
            <MenuItem value="regular">A4 (Regular)</MenuItem>
            <MenuItem value="thermal">Thermal 80 mm</MenuItem>
          </TextField>
        )}
        <Button variant="contained" onClick={generate} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PreviewIcon />}>
          Generate preview
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {src ? (
        <Box component="iframe" title="PDF preview" src={src} sx={{ width: '100%', height: 820, border: '1px solid', borderColor: 'divider', borderRadius: 1 }} />
      ) : (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>Choose a document and press Generate preview.</Paper>
      )}
    </Box>
  )
}

export const CompanyProfilePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = TAB_NAMES.indexOf(searchParams.get('tab') || 'general')
  const activeTab = requested >= 0 ? requested : 0
  const setActiveTab = (index: number) => setSearchParams(index === 0 ? {} : { tab: TAB_NAMES[index] }, { replace: true })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState<Partial<CompanyProfile>>({})
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const loadProfile = async () => {
    try {
      setLoading(true)
      const data = await companyProfileService.getProfile()
      setProfile(data)
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to load company profile',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProfile()
  }, [])

  const handleChange = (field: keyof CompanyProfile, value: any) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const updated = await companyProfileService.updateProfile(profile)
      setProfile(updated)
      setSnackbar({
        open: true,
        message: 'Company master data & branding saved successfully!',
        severity: 'success',
      })
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: errorText(err, 'Failed to save company profile'),
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${profile.primary_color || '#DC2626'} 0%, ${profile.secondary_color || '#1F2937'} 100%)`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
            }}
          >
            <BusinessIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Company Profile & Brand Master
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              One place for company details, tax and bank information, logo and brand colours, document templates, numbering and PDF previews. Every invoice, estimate, challan, ledger and catalogue reads from here.
            </Typography>
          </Box>
        </Box>
        {activeTab < PROFILE_TAB_COUNT && (<Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={loadProfile}
            disabled={saving}
            startIcon={<RefreshIcon />}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{
              bgcolor: '#2563EB',
              color: 'white',
              fontWeight: 600,
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {saving ? 'Saving...' : 'Save Master Data'}
          </Button>
        </Stack>)}
      </Paper>

      {profile.incomplete_fields && profile.incomplete_fields.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>Some details are missing or still placeholders</strong> and will not print on documents:{' '}
          {profile.incomplete_fields.map((f) => FIELD_LABELS[f] || f).join(', ')}.
        </Alert>
      )}

      {/* Tabs Layout */}
      <Card elevation={1} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 56,
              },
            }}
          >
            <Tab icon={<BusinessIcon />} iconPosition="start" label="General Information" />
            <Tab icon={<ContactIcon />} iconPosition="start" label="Contact & WhatsApp" />
            <Tab icon={<LocationIcon />} iconPosition="start" label="Registered Address" />
            <Tab icon={<TaxIcon />} iconPosition="start" label="Tax & Legal (GST/PAN)" />
            <Tab icon={<BrandingIcon />} iconPosition="start" label="Branding & Colors" />
            <Tab icon={<BankIcon />} iconPosition="start" label="Banking & UPI" />
            <Tab icon={<DocumentNotesIcon />} iconPosition="start" label="Document Notes & Terms" />
            <Tab icon={<TemplatesIcon />} iconPosition="start" label="Document Templates" />
            <Tab icon={<NumberingIcon />} iconPosition="start" label="Numbering" />
            <Tab icon={<PreviewIcon />} iconPosition="start" label="PDF Preview" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {/* TAB 0: General Info */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Basic Business Details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Display name and legal corporate entity name used across invoices, estimates, and customer notifications.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Display Brand / Trade Name *"
                  value={profile.company_name || ''}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="e.g. Dhanshika Exports"
                  helperText="Primary brand name displayed on headers, portal, and customer messages"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Legal Entity Name"
                  value={profile.legal_name || ''}
                  onChange={(e) => handleChange('legal_name', e.target.value)}
                  placeholder="e.g. Dhanshika Exports Imports Pvt. Ltd."
                  helperText="Registered business entity name printed on official tax invoices"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tagline / Slogan"
                  value={profile.tagline || ''}
                  onChange={(e) => handleChange('tagline', e.target.value)}
                  placeholder="e.g. Premier B2B Export & Trading Partner"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Official Website URL"
                  value={profile.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="e.g. https://dhanshika.com"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 1: Contact & WhatsApp */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Communication & Support Numbers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Used dynamically in transactional emails, automated WhatsApp alerts, and payment receipts.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary Contact / WhatsApp Number *"
                  value={profile.whatsapp_number || ''}
                  onChange={(e) => handleChange('whatsapp_number', e.target.value)}
                  placeholder="e.g. +91 9876543210"
                  helperText="Business phone and WhatsApp number sent to customers for order updates"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Alternate Office Phone / Landline"
                  value={profile.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="e.g. +91 1234567890"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="General Business Email *"
                  value={profile.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="e.g. info@dhanshika.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Support Email"
                  value={profile.support_email || ''}
                  onChange={(e) => handleChange('support_email', e.target.value)}
                  placeholder="e.g. support@dhanshika.com"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 2: Address */}
          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Registered Office & Dispatch Address
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Official business address printed on Tax Invoices, Delivery Challans, and Shipping Labels.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 1 *"
                  value={profile.address_line1 || ''}
                  onChange={(e) => handleChange('address_line1', e.target.value)}
                  placeholder="e.g. Ground Floor, Plot No. 297, Khata No. 1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={profile.address_line2 || ''}
                  onChange={(e) => handleChange('address_line2', e.target.value)}
                  placeholder="e.g. JS College Road, Nimiya, Opposite JS College"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="City *"
                  value={profile.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g. Daltonganj"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="State *"
                  value={profile.state || ''}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="e.g. Jharkhand"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="GST State Code *"
                  value={profile.state_code || ''}
                  onChange={(e) => handleChange('state_code', e.target.value.replace(/\D/g, '').slice(0, 2))}
                  placeholder="e.g. 20"
                  helperText="First 2 digits of your GSTIN; decides CGST/SGST vs IGST"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Pincode *"
                  value={profile.pincode || ''}
                  onChange={(e) => handleChange('pincode', e.target.value)}
                  placeholder="e.g. 822101"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Country *"
                  value={profile.country || ''}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="e.g. India"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 3: Tax & Legal */}
          {activeTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  GSTIN & Statutory Registrations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  GSTIN, PAN, and Corporate Identification Number for statutory compliance.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="GSTIN *"
                  value={profile.gstin || ''}
                  onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                  placeholder="e.g. 20AABCD1234E1Z5"
                  helperText="15-character Goods & Services Tax Identification Number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Permanent Account Number (PAN)"
                  value={profile.pan || ''}
                  onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                  placeholder="e.g. AABCD1234E"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Corporate Identity Number (CIN)"
                  value={profile.cin || ''}
                  onChange={(e) => handleChange('cin', e.target.value.toUpperCase())}
                  placeholder="e.g. U51909JH2022PTC018234"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 4: Branding & Colors */}
          {activeTab === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Brand Identity & Theme Palette
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload your logo, signature and stamp and choose the brand colours used on every PDF, email and notification.
                </Typography>
              </Grid>

              {([
                ['logo', 'Primary Logo', 'Shown on every PDF, email and the portal (PNG with a transparent background works best)', 'logo_url'],
                ['dark_logo', 'Dark / Reverse Logo', 'Optional, for dark backgrounds', 'dark_logo_url'],
                ['signature', 'Authorised Signature', 'Printed above "Authorised Signatory" on invoices', 'signature_url'],
                ['stamp', 'Company Stamp', 'Printed next to the signature', 'stamp_url'],
                ['favicon', 'Favicon', 'Small browser-tab icon', 'favicon_url'],
              ] as [BrandingAssetKind, string, string, keyof CompanyProfile][]).map(([kind, label, helper, field]) => (
                <Grid item xs={12} sm={6} md={4} key={kind}>
                  <AssetUploader
                    label={label}
                    helper={helper}
                    kind={kind}
                    url={profile[field] as string | undefined}
                    onUploaded={(url) => {
                      handleChange(field, url)
                      setSnackbar({ open: true, message: `${label} uploaded`, severity: 'success' })
                    }}
                    onRemove={() => handleChange(field, null)}
                    onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Uploads are saved immediately. Removing an image takes effect after you press Save Master Data.
                </Typography>
              </Grid>

              {/* Colors */}
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Primary Brand Colour (headers, banners, totals)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      value={profile.primary_color || '#DC2626'}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      style={{ width: 44, height: 44, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <TextField
                      size="small"
                      value={profile.primary_color || '#DC2626'}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5, p: 1, bgcolor: profile.primary_color || '#DC2626', color: 'white', borderRadius: 1, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>
                    Primary Preview
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Text / Dark Colour (body text, footer bars)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      value={profile.secondary_color || '#1F2937'}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      style={{ width: 44, height: 44, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <TextField
                      size="small"
                      value={profile.secondary_color || '#1F2937'}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5, p: 1, bgcolor: profile.secondary_color || '#1F2937', color: 'white', borderRadius: 1, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>
                    Secondary Preview
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Accent Colour
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="color"
                      value={profile.accent_color || '#2563EB'}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      style={{ width: 44, height: 44, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <TextField
                      size="small"
                      value={profile.accent_color || '#2563EB'}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5, p: 1, bgcolor: profile.accent_color || '#2563EB', color: 'white', borderRadius: 1, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>
                    Accent Preview
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* TAB 5: Banking & UPI */}
          {activeTab === 5 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Bank Account & UPI Payment Destination
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Bank details and UPI ID printed on PDF Invoices for customer NEFT/RTGS/IMPS/UPI payments.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  value={profile.bank_account_holder || ''}
                  onChange={(e) => handleChange('bank_account_holder', e.target.value)}
                  placeholder="Leave blank to use the legal company name"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={profile.bank_name || ''}
                  onChange={(e) => handleChange('bank_name', e.target.value)}
                  placeholder="e.g. HDFC Bank"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={profile.bank_account_number || ''}
                  onChange={(e) => handleChange('bank_account_number', e.target.value)}
                  placeholder="e.g. 50200012345678"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  value={profile.bank_ifsc || ''}
                  onChange={(e) => handleChange('bank_ifsc', e.target.value.toUpperCase())}
                  placeholder="e.g. HDFC0001234"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Branch Name"
                  value={profile.bank_branch || ''}
                  onChange={(e) => handleChange('bank_branch', e.target.value)}
                  placeholder="e.g. Daltonganj Branch"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Business UPI ID"
                  value={profile.upi_id || ''}
                  onChange={(e) => handleChange('upi_id', e.target.value)}
                  placeholder="e.g. dhanshika@bandhan"
                />
              </Grid>
            </Grid>
          )}

          {/* TAB 6: Document Notes & Terms */}
          {activeTab === 6 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Default Invoice Notes, Footers & Terms
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Default headers, thank-you notes, and legal jurisdiction terms automatically populated on generated documents.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Invoice / Estimate Header Note"
                  value={profile.invoice_header_note || ''}
                  onChange={(e) => handleChange('invoice_header_note', e.target.value)}
                  placeholder="e.g. Note: Subject to stock availability at dispatch time."
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Invoice Footer Note / Thank You Note"
                  value={profile.invoice_footer_note || ''}
                  onChange={(e) => handleChange('invoice_footer_note', e.target.value)}
                  placeholder="e.g. Thank you for your business! Goods once sold are subject to company terms."
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Standard Terms & Conditions"
                  value={profile.terms_and_conditions || ''}
                  onChange={(e) => handleChange('terms_and_conditions', e.target.value)}
                  placeholder="1. Payment terms as agreed..."
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          )}
          {activeTab === 7 && <InvoiceTemplateConfigurationPage />}
          {activeTab === 8 && <DocumentSequenceConfigurationPage />}
          {activeTab === 9 && <PdfPreviewPanel />}
        </CardContent>

        {activeTab < PROFILE_TAB_COUNT && (<><Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{
              bgcolor: '#DC2626',
              fontWeight: 600,
              '&:hover': { bgcolor: '#B91C1C' },
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box></>)}
      </Card>

      {/* Snackbar Alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CompanyProfilePage
